import {
  AxiosError,
  AxiosHeaders,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from 'axios'
import i18next from 'i18next'
import * as Sentry from '@sentry/react'
import Config from 'app/utils/config'
import AuthToken from 'app/models/Auth'

import { KhetiAxiosConfig, configureKhetiAxiosInstance } from './axios'

import keycloak from './keycloak'
import fetchTokenFromRefreshToken from './apis/fetchTokenFromRefreshToken'
import { logout } from './common'
import { getCookies, setCookies } from './storage'
import khetiAxiosInstance from './axios/kheti/config'

interface RetriableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean
}

let refreshTokenPromise: Promise<AuthToken | null> | null = null
let logoutPromise: Promise<void> | null = null

const interceptorCb = (axiosConfig: RetriableRequestConfig) => {
  const token = keycloak.token
  const headers = AxiosHeaders.from(axiosConfig.headers)
  headers.set('X-AUTH-SCHEME', 'keycloak')
  headers.set('X-LANGUAGE', i18next.language)

  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  axiosConfig.headers = headers
  return axiosConfig
}

const responseInterceptorSuccessCb = (response: AxiosResponse) => response

const syncTokensInKeycloak = (tokens: Partial<AuthToken>) => {
  if (tokens.access_token) {
    keycloak.token = tokens.access_token
  }
  if (tokens.refresh_token) {
    keycloak.refreshToken = tokens.refresh_token
  }
  if (tokens.id_token) {
    keycloak.idToken = tokens.id_token
  }
}

const refreshTokens = async (): Promise<AuthToken | null> => {
  const cookies = getCookies<Partial<AuthToken>>()
  const refreshToken = keycloak.refreshToken || cookies.refresh_token
  if (!refreshToken || !Config.OIDC_URL || !Config.CLIENT_ID) {
    return null
  }

  const response = await fetchTokenFromRefreshToken(
    Config.OIDC_URL,
    refreshToken,
    Config.CLIENT_ID,
  )
  if (!response.ok) {
    return null
  }

  const refreshedTokens = (await response.json()) as Partial<AuthToken>
  if (!refreshedTokens.access_token || !refreshedTokens.refresh_token) {
    return null
  }

  const mergedTokens: AuthToken = {
    access_token: refreshedTokens.access_token,
    refresh_token: refreshedTokens.refresh_token,
    id_token: refreshedTokens.id_token || cookies.id_token || '',
    expires_in: Number(refreshedTokens.expires_in || cookies.expires_in || 0),
    refresh_expires_in: Number(
      refreshedTokens.refresh_expires_in || cookies.refresh_expires_in || 0,
    ),
  }

  syncTokensInKeycloak(mergedTokens)
  // Keep cookie state aligned so app reload/session restore uses fresh tokens.
  setCookies({
    access_token: mergedTokens.access_token,
    refresh_token: mergedTokens.refresh_token,
    id_token: mergedTokens.id_token,
  })

  return mergedTokens
}

const getRefreshedTokens = async () => {
  if (!refreshTokenPromise) {
    // Single-flight refresh: concurrent 401 handlers reuse one refresh request.
    refreshTokenPromise = refreshTokens().finally(() => {
      refreshTokenPromise = null
    })
  }
  return refreshTokenPromise
}

const logoutOnce = async () => {
  if (!logoutPromise) {
    // Single-flight logout: avoid triggering multiple parallel logout redirects.
    logoutPromise = logout().finally(() => {
      logoutPromise = null
    })
  }
  return logoutPromise
}

const responseInterceptorErrCb = async (err: AxiosError) => {
  const status = err.response?.status
  const originalRequest = err.config as RetriableRequestConfig | undefined
  const isTokenEndpoint = Boolean(
    originalRequest?.url?.includes('/protocol/openid-connect/token'),
  )

  if (status !== 401 || !originalRequest || isTokenEndpoint) {
    return Promise.reject(err)
  }

  // If retry already happened and still got 401, auth session is invalid.
  if (originalRequest._retry) {
    await logoutOnce()
    return Promise.reject(err)
  }

  originalRequest._retry = true

  try {
    const refreshedTokens = await getRefreshedTokens()
    if (!refreshedTokens?.access_token) {
      await logoutOnce()
      return Promise.reject(err)
    }

    // Replay only this failed request once with the refreshed bearer token.
    const headers = AxiosHeaders.from(originalRequest.headers)
    headers.set('Authorization', `Bearer ${refreshedTokens.access_token}`)
    originalRequest.headers = headers

    return khetiAxiosInstance.request(originalRequest)
  } catch {
    await logoutOnce()
    return Promise.reject(err)
  }
}

const khetiConfig: KhetiAxiosConfig = {
  appCode: Config.APP_CODE as string,
  khetiUrl: Config.KHETI_URL as string,
  requestInterceptorSuccessCb: interceptorCb,
  responseInterceptorSuccessCb: responseInterceptorSuccessCb,
  responseInterceptorErrCb: responseInterceptorErrCb,
}

export function initAxiosInstances() {
  // configure the axios instance
  configureKhetiAxiosInstance(khetiConfig)
}

export function initSentry() {
  if (process.env.NODE_ENV === 'production' && Config.SENTRY_DSN) {
    Sentry.init({
      dsn: Config.SENTRY_DSN,
      tracesSampleRate: 0.25,
      environment: process.env.NODE_ENV,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
      ],
      _experiments: {
        // The sampling rate for profiling is relative to TracesSampleRate.
        // In this case, we'll capture profiles for 10% of transactions.
        profilesSampleRate: 0.1,
      },
      // Session Replay
      replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    })
  }
}

export const loadGoogleMaps = () => {
  if (window.google) return

  const script = document.createElement('script')
  script.src = `https://maps.googleapis.com/maps/api/js?key=${Config.GOOGLE_MAPS_KEY}&libraries=places`
  script.async = true
  script.defer = true

  document.head.appendChild(script)
}
